// Public email preference centre — no auth required.
// Authenticated by unsubscribe_token from the URL query param.
// Route: /email-preferences?token=<uuid>

import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";

// ── Types ─────────────────────────────────────────────────────────────────────

interface Prefs {
  memory_prompts: boolean;
  acorns_challenges: boolean;
  forest_news: boolean;
}

type PrefKey = keyof Prefs;

// ── Category metadata ─────────────────────────────────────────────────────────

const CATEGORIES: Array<{
  key: PrefKey;
  label: string;
  description: string;
}> = [
  {
    key: "memory_prompts",
    label: "Memory nudges",
    description: "Gentle reminders to capture moments before they slip away.",
  },
  {
    key: "acorns_challenges",
    label: "Acorns & progress",
    description: "Updates on your streaks, acorns, and new challenges.",
  },
  {
    key: "forest_news",
    label: "News from the forest",
    description: "New features and stories from the Mori team.",
  },
];

// ── Confirmation dialog copy ───────────────────────────────────────────────────

const CONFIRM_OFF: Record<PrefKey, { title: string; body: string; cancel: string; confirm: string }> = {
  memory_prompts: {
    title: "Going quieter?",
    body: "Mori will stop sending memory prompts. Your memories are safe — he'll just be a little quieter.",
    cancel: "Keep them",
    confirm: "Go quiet",
  },
  acorns_challenges: {
    title: "Pause the updates?",
    body: "Mori won't send acorn or streak updates. Your progress is still saved — he'll just stop nudging.",
    cancel: "Keep them",
    confirm: "Pause updates",
  },
  forest_news: {
    title: "Leave the forest?",
    body: "Mori will stop sharing news from the forest. You can always come back to catch up.",
    cancel: "Stay",
    confirm: "Leave for now",
  },
};

// ── Toggle component ──────────────────────────────────────────────────────────

function Toggle({ checked, onChange, disabled }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-1 disabled:cursor-not-allowed ${
        checked ? "bg-orange-500" : "bg-gray-200"
      }`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-6" : "translate-x-1"
        }`}
      />
    </button>
  );
}

// ── Skeleton row ──────────────────────────────────────────────────────────────

function SkeletonRow() {
  return (
    <div className="flex items-center justify-between py-4 border-b border-gray-100 animate-pulse">
      <div className="space-y-1.5">
        <div className="h-4 w-32 bg-gray-200 rounded" />
        <div className="h-3 w-52 bg-gray-100 rounded" />
      </div>
      <div className="h-7 w-12 bg-gray-200 rounded-full shrink-0" />
    </div>
  );
}

// ── Confirmation dialog ────────────────────────────────────────────────────────

function ConfirmDialog({
  open,
  prefKey,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  prefKey: PrefKey | null;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  if (!open || !prefKey) return null;
  const copy = CONFIRM_OFF[prefKey];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 space-y-4">
        <h2 className="text-base font-semibold text-gray-900">{copy.title}</h2>
        <p className="text-sm text-gray-600 leading-relaxed">{copy.body}</p>
        <div className="flex items-center justify-end gap-3 pt-1">
          <button
            onClick={onCancel}
            className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            {copy.cancel}
          </button>
          <button
            onClick={onConfirm}
            className="rounded-xl bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 transition-colors"
          >
            {copy.confirm}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function EmailPreferences() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [prefs, setPrefs] = useState<Prefs | null>(null);
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);
  const [saving, setSaving] = useState(false);

  // Confirmation state
  const [confirmKey, setConfirmKey] = useState<PrefKey | null>(null);
  const [pendingValue, setPendingValue] = useState<boolean | null>(null);

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

  const fnBase = `${supabaseUrl}/functions/v1/manage-email-preferences`;

  // ── Load preferences on mount ────────────────────────────────────────────

  useEffect(() => {
    if (!token) {
      setInvalid(true);
      setLoading(false);
      return;
    }

    fetch(`${fnBase}?token=${encodeURIComponent(token)}`, {
      headers: { apikey: anonKey },
    })
      .then(async (res) => {
        if (res.status === 404) {
          setInvalid(true);
        } else if (res.ok) {
          const data = (await res.json()) as Prefs;
          setPrefs(data);
        } else {
          setInvalid(true);
        }
      })
      .catch(() => setInvalid(true))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ── Toggle handler ────────────────────────────────────────────────────────

  function handleToggle(key: PrefKey, value: boolean) {
    if (!prefs || saving) return;
    if (!value) {
      // Toggling OFF — show confirmation first
      setConfirmKey(key);
      setPendingValue(value);
    } else {
      // Toggling ON — apply immediately
      void applyUpdate(key, value);
    }
  }

  async function applyUpdate(key: PrefKey, value: boolean) {
    if (!prefs || !token) return;
    const optimistic = { ...prefs, [key]: value };
    setPrefs(optimistic);
    setSaving(true);

    try {
      const res = await fetch(`${fnBase}?token=${encodeURIComponent(token)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", apikey: anonKey },
        body: JSON.stringify(optimistic),
      });

      if (res.ok) {
        const result = (await res.json()) as { success: boolean; updated: Prefs };
        if (result.success) {
          setPrefs(result.updated);
          toast("Mori updated your preferences 🌰", {
            style: { background: "#FFF8F0", border: "1px solid #EDE0D4", color: "#1A0F00" },
          });
        }
      } else {
        // Revert on failure
        setPrefs(prefs);
        toast.error(t("toast.genericError"));
      }
    } catch {
      setPrefs(prefs);
      toast.error(t("toast.genericError"));
    } finally {
      setSaving(false);
    }
  }

  function handleConfirm() {
    const key = confirmKey;
    const value = pendingValue;
    setConfirmKey(null);
    setPendingValue(null);
    if (key !== null && value !== null) {
      void applyUpdate(key, value);
    }
  }

  function handleCancel() {
    setConfirmKey(null);
    setPendingValue(null);
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-white flex flex-col items-center px-4 py-12">
      <Toaster position="top-center" />

      <div className="w-full max-w-[480px]">
        {/* Mori mark */}
        <div className="text-center mb-8">
          <span className="text-6xl leading-none" role="img" aria-label="Mori">🌰</span>
          <h1
            className="mt-4 text-2xl font-medium text-gray-900 leading-tight"
            style={{ fontFamily: "Georgia, serif" }}
          >
            Which stories should Mori bring you?
          </h1>
          <p className="mt-2 text-sm text-gray-500">
            Mori keeps your memories — you choose what he sends.
          </p>
        </div>

        {/* Error state */}
        {invalid && (
          <div className="text-center py-8 px-4">
            <p className="text-gray-500 text-sm leading-relaxed">
              This link has expired or is no longer valid. Open the app to manage your settings.
            </p>
          </div>
        )}

        {/* Loading skeleton */}
        {loading && !invalid && (
          <div className="space-y-1">
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </div>
        )}

        {/* Preferences list */}
        {prefs && !loading && (
          <div className="space-y-1">
            {CATEGORIES.map((cat) => (
              <div
                key={cat.key}
                className="flex items-center justify-between py-4 border-b border-gray-100"
              >
                <div className="pr-4">
                  <p className="text-sm font-semibold text-gray-900">{cat.label}</p>
                  <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{cat.description}</p>
                </div>
                <Toggle
                  checked={prefs[cat.key]}
                  onChange={(v) => handleToggle(cat.key, v)}
                  disabled={saving}
                />
              </div>
            ))}

            {/* Locked: account security */}
            <div className="mt-6 rounded-2xl bg-amber-50 border border-amber-100 px-4 py-4 flex items-start gap-3">
              <span className="text-amber-500 mt-0.5 text-base leading-none" role="img" aria-label="lock">🔒</span>
              <div>
                <p className="text-sm font-semibold text-amber-900">{t("emailPreferences.essentials")}</p>
                <p className="text-xs text-amber-700 mt-0.5 leading-relaxed">
                  Mori needs these to keep your account safe. He'd never turn them off.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!confirmKey}
        prefKey={confirmKey}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </div>
  );
}
